import { createContext, ReactNode, useContext, useState, useMemo, useCallback } from 'react';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import RecurringRulesForm from './RecurringRulesForm';

interface RecurringRulesFormContextType {
  closeModal: () => void;
  isOpen: boolean;
  openModal: (takerMarkets: TakerMarket[]) => void;
  takerMarkets: TakerMarket[] | null;
}

interface RecurringRulesFormProviderProps {
  children: ReactNode;
}

const RecurringRulesFormContext = createContext<RecurringRulesFormContextType | undefined>(undefined);

export const useRecurringRulesForm = () => {
  const context = useContext(RecurringRulesFormContext);

  if (!context) {
    throw new Error('useRecurringRulesForm must be used within a RecurringRulesFormProvider');
  }

  return context;
};

const RecurringRulesFormProvider = ({ children }: RecurringRulesFormProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [takerMarkets, setTakerMarkets] = useState<TakerMarket[] | null>(null);

  const openModal = useCallback((markets: TakerMarket[]) => {
    setTakerMarkets(markets);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);

    // clear takerMarkets after modal is closed
    setTimeout(() => {
      setTakerMarkets(null);
    }, 300);
  }, []);

  const contextValue = useMemo(
    () => ({
      closeModal,
      isOpen,
      openModal,
      takerMarkets,
    }),
    [closeModal, isOpen, openModal, takerMarkets]
  );

  return <RecurringRulesFormContext.Provider value={contextValue}>{children}</RecurringRulesFormContext.Provider>;
};

const RecurringRulesFormComponent = () => {
  const { closeModal, isOpen, takerMarkets } = useRecurringRulesForm();

  return takerMarkets ? (
    <RecurringRulesForm onClose={closeModal} open={isOpen} referrer="buyerControls" takerMarkets={takerMarkets} />
  ) : null;
};

export const RecurringRulesFormWithContext = ({ children }: { children: ReactNode }) => {
  return (
    <RecurringRulesFormProvider>
      <RecurringRulesFormComponent />
      {children}
    </RecurringRulesFormProvider>
  );
};
